import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, Form, Input } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  PhoneOutlined,
  LockOutlined,
  LoginOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  CalendarOutlined,
  SearchOutlined,
  UserOutlined,
  MailOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

// ── Shared CSS (same as PatientPortal) ────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');

  .fp-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .fp-root {
    min-height: 100vh; display: flex; align-items: stretch;
    font-family: 'DM Sans', sans-serif; background: #f0f4f8;
  }
  .fp-left {
    width: 38%;
    background: linear-gradient(160deg, #074f9bff 0%, #0b61bdff 55%, #007BFF 100%);
    display: flex; flex-direction: column; justify-content: center;
    gap: 36px; padding: 52px 44px; position: relative; overflow: hidden;
  }
  .fp-left::before {
    content: ''; position: absolute; top: -80px; right: -80px;
    width: 320px; height: 320px; border-radius: 50%;
    background: rgba(255,255,255,0.05);
  }
  .fp-left::after {
    content: ''; position: absolute; bottom: -120px; left: -60px;
    width: 400px; height: 400px; border-radius: 50%;
    background: rgba(255,255,255,0.04);
  }
  .fp-logo-icon {
    width: 64px; height: 64px; background: rgba(255,255,255,0.15);
    border-radius: 18px; display: flex; align-items: center; justify-content: center;
    font-size: 28px; color: #fff; margin-bottom: 24px;
    backdrop-filter: blur(4px); border: 1px solid rgba(255,255,255,0.2);
  }
  .fp-brand-block { position: relative; z-index: 1; }
  .fp-brand-title {
    font-family: 'Playfair Display', serif; font-size: 28px; font-weight: 700;
    color: #fff; line-height: 1.2; margin-bottom: 10px;
  }
  .fp-brand-sub { font-size: 14px; color: rgba(255,255,255,0.72)!important; line-height: 1.6; }
  .fp-features { display: flex; flex-direction: column; gap: 14px; position: relative; z-index: 1; }
  .fp-feat-item {
    display: flex; align-items: center; gap: 14px;
    color: rgba(255,255,255,0.88); font-size: 14px; font-weight: 500;
  }
  .fp-feat-icon {
    width: 38px; height: 38px; background: rgba(255,255,255,0.12);
    border-radius: 10px; display: flex; align-items: center; justify-content: center;
    font-size: 16px; flex-shrink: 0; border: 1px solid rgba(255,255,255,0.15);
  }
  .fp-left-footer {
    font-size: 12px; color: rgba(255,255,255,0.45);
    position: absolute; bottom: 28px; left: 44px; right: 44px; z-index: 1;
  }
  .fp-right {
    flex: 1; display: flex; align-items: center; justify-content: center;
    padding: 40px 24px; background: #f8fafc;
  }
  .fp-form-box { width: 100%; max-width: 460px; }
  .fp-steps { display: flex; align-items: flex-start; margin-bottom: 32px; }
  .fp-step {
    display: flex; flex-direction: column; align-items: center;
    gap: 6px; flex: 1; position: relative;
  }
  .fp-step:not(:last-child)::after {
    content: ''; position: absolute; top: 16px; left: 50%;
    width: 100%; height: 2px; background: #e2e8f0; z-index: 0;
  }
  .fp-step.done::after  { background: #007BFF; }
  .fp-step.active::after { background: #007BFF; }
  .fp-step-dot {
    width: 32px; height: 32px; border-radius: 50%; background: #e2e8f0;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 600; color: #94a3b8;
    position: relative; z-index: 1; transition: all 0.3s ease;
  }
  .fp-step.active .fp-step-dot {
    background: #217559; color: #fff;
    box-shadow: 0 0 0 4px rgba(33,117,89,0.15);
  }
  .fp-step.done .fp-step-dot { background: #007BFF; color: #fff; }
  .fp-step-label { font-size: 11px; color: #94a3b8; font-weight: 500; white-space: nowrap; }
  .fp-step.active .fp-step-label { color: #0854a5ff; font-weight: 600; }
  .fp-step.done  .fp-step-label { color: #007BFF; }
  .fp-form-title {
    font-family: 'Playfair Display', serif;
    font-size: 26px; font-weight: 700; color: #0f172a; margin-bottom: 6px;
  }
  .fp-form-sub { font-size: 14px; color: #64748b; margin-bottom: 28px; }
  .fp-input .ant-input,
  .fp-input .ant-input-affix-wrapper {
    border-radius: 10px !important; border: 1.5px solid #e2e8f0 !important;
    padding: 12px 14px !important; font-size: 14px !important;
    background: #fff !important; transition: all 0.2s !important;
  }
  .fp-input .ant-input:focus,
  .fp-input .ant-input-affix-wrapper-focused {
    border-color: #217559 !important;
    box-shadow: 0 0 0 3px rgba(33,117,89,0.1) !important;
  }
  .fp-input .ant-form-item-label label {
    font-weight: 600 !important; color: #374151 !important; font-size: 13px !important;
  }
  .fp-btn {
    height: 50px !important; border-radius: 12px !important;
    font-weight: 600 !important; font-size: 15px !important; letter-spacing: 0.3px !important;
    background: linear-gradient(135deg, #0b5aaeff, #007BFF) !important;
    border: none !important; transition: all 0.2s !important;
  }
  .fp-btn:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(33,117,89,0.35) !important; opacity: 0.95 !important;
  }
  .fp-btn-ghost {
    height: 50px !important; border-radius: 12px !important;
    font-weight: 600 !important; font-size: 15px !important;
    background: #f1f5f9 !important; border: 1.5px solid #e2e8f0 !important;
    color: #374151 !important;
  }
  .fp-patient-card {
    background: linear-gradient(135deg, #f0fdf4, #dcfce7);
    border: 1.5px solid #bbf7d0; border-radius: 14px; padding: 18px 20px;
    margin-bottom: 22px; display: flex; align-items: center; gap: 16px;
  }
  .fp-patient-avatar {
    width: 52px; height: 52px; border-radius: 50%;
    background: linear-gradient(135deg, #217559, #14a876);
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; color: #fff; flex-shrink: 0;
  }
  .fp-patient-name  { font-weight: 700; font-size: 16px; color: #0f172a; margin-bottom: 3px; }
  .fp-patient-meta  { font-size: 12px; color: #64748b; display: flex; flex-direction: column; gap: 2px; }
  .fp-otp-row       { display: flex; gap: 10px; justify-content: center; margin-bottom: 24px; }
  .fp-success-icon {
    width: 80px; height: 80px; border-radius: 50%;
    background: linear-gradient(135deg, #0457b0ff, #007BFF);
    display: flex; align-items: center; justify-content: center;
    font-size: 38px; color: #fff; margin: 0 auto 24px;
    box-shadow: 0 8px 24px rgba(33,117,89,0.3);
    animation: fp-pop 0.5s cubic-bezier(.34,1.56,.64,1) both;
  }
  @keyframes fp-pop {
    from { transform: scale(0); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }
  .fp-link { color: #007BFF; font-weight: 600; text-decoration: none; }
  .fp-link:hover { text-decoration: underline; }
  @media (max-width: 768px) {
    .fp-left { display: none; }
    .fp-right { padding: 30px 16px; }
  }

  .pp-logo-icon {
  width: 72px;
  height: 72px;
  background: rgba(255,255,255,0.15);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.2);
}

.pp-logo-image {
  width: 56px;
  height: 56px;
  object-fit: contain;
}
`;

// ── OTP Timer (reused from PatientPortal) ─────────────────────────
const OTPTimerDisplay = ({ resetKey, onResend }) => {
  const [timer, setTimer]       = useState(60);
  const [canResend, setCanResend] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    setTimer(60);
    setCanResend(false);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) { clearInterval(intervalRef.current); setCanResend(true); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [resetKey]);

  return (
    <div style={{ textAlign: "center", marginBottom: 20, fontSize: 13, color: "#64748b" }}>
      {canResend ? (
        <button
          onClick={onResend}
          style={{ color: "#217559", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
        >
          <ReloadOutlined style={{ marginRight: 5 }} />Resend OTP
        </button>
      ) : (
        <span>Resend in <strong style={{ color: "#217559" }}>{timer}s</strong></span>
      )}
    </div>
  );
};

// ── STEPS ──────────────────────────────────────────────────────────
const STEPS = {
  SEARCH:  "search",
  FOUND:   "found",
  OTP:     "otp",
  RESET:   "reset",
  SUCCESS: "success",
};
const STEP_LABELS = ["Search", "Verify", "Reset", "Done"];

// ── MAIN COMPONENT ─────────────────────────────────────────────────
function ForgetPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchForm]   = Form.useForm();
  const [passwordForm] = Form.useForm();

  const [step,        setStep]        = useState(STEPS.SEARCH);
  const [isLoading,   setIsLoading]   = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [otp,         setOtp]         = useState(Array(6).fill(""));
  const [otpResetKey, setOtpResetKey] = useState(0);

  const otpRefs = useRef([]);

  // Helper to mask phone
  const maskPhoneLast4 = (p = "") => {
    if (!p) return "";
    const visible = p.slice(-4);
    return "*******" + visible;
  };
  const maskEmail = (e = "") => { const [l, d] = e.split("@"); return l.slice(0, 2) + "***@" + d; };
  const maskPhone = (p = "") => p.slice(0, 4) + "****" + p.slice(-3);

  // ── API calls ──────────────────────────────────────────────────

  const handleSearch = async (values) => {
    setIsLoading(true);
    try {
      dispatch(showLoading());
      const res = await axios.post("/api/patient/phone-lookup", { phone: values.searchPhone });
      dispatch(hideLoading());
      if (res.data.success && res.data.patient) {
        setPatientData(res.data.patient);
        setStep(STEPS.FOUND);
      } else {
        toast.error(res.data.message || "Patient not found");
      }
    } catch { dispatch(hideLoading()); toast.error("Something went wrong"); }
    finally  { setIsLoading(false); }
  };

  const handleSendOTP = async () => {
    setIsLoading(true);
    try {
      dispatch(showLoading());
      const payload = {
        patientId: patientData.id,
        email:     patientData.email,
        phone:     patientData.phone,
        fullName:  patientData.fullName,
        type:      "reset", // explicitly for password reset
      };
      const res = await axios.post("/api/patient/reset-send-otp", payload);
      dispatch(hideLoading());
      toast.success(res.data.message);
      setOtpResetKey((k) => k + 1);
      setStep(STEPS.OTP);
    } catch (err) {
      dispatch(hideLoading());
      const status = err.response?.status;
      const data   = err.response?.data;
      if (status === 404) {
        toast.error("No portal account found. Please register first.");
        setTimeout(() => { setPatientData(null); setStep(STEPS.SEARCH); }, 2000);
        return;
      }
      if (status === 403) {
        toast.error(data?.message || "Account inactive. Contact support.");
        setTimeout(() => { setPatientData(null); setStep(STEPS.SEARCH); }, 2000);
        return;
      }
      toast.error(data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtp(Array(6).fill(""));
    await handleSendOTP();
  };

  const handleVerifyOTP = async () => {
    const code = otp.join("");
    if (code.length < 6) { toast.error("Enter all 6 digits"); return; }
    setIsLoading(true);
    try {
      dispatch(showLoading());
      const res = await axios.post("/api/patient/reset-verify-otp", { patientId: patientData.id, otp: code });
      dispatch(hideLoading());
      if (res.data.success) {
        toast.success("OTP verified!");
        setStep(STEPS.RESET);
      } else {
        toast.error(res.data.message || "Invalid OTP");
      }
    } catch { dispatch(hideLoading()); toast.error("Verification failed"); }
    finally  { setIsLoading(false); }
  };

  const handleResetPassword = async (values) => {
    setIsLoading(true);
    try {
      dispatch(showLoading());
      const res = await axios.post("/api/patient/reset-password", {
        patientId: patientData.id,
        password:  values.password,
      });
      dispatch(hideLoading());
      if (res.data.success) {
        setStep(STEPS.SUCCESS);
      } else {
        toast.error(res.data.message);
      }
    } catch { dispatch(hideLoading()); toast.error("Something went wrong"); }
    finally  { setIsLoading(false); }
  };

  // ── OTP handlers (same as PatientPortal) ──────────────────────
  const focusInput = useCallback((index) => {
    setTimeout(() => {
      otpRefs.current[index]?.focus();
      otpRefs.current[index]?.select();
    }, 0);
  }, []);

  const handleOtpInput = useCallback((e, i) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setOtp((prev) => { const next = [...prev]; next[i] = ""; return next; });
      return;
    }
    if (raw.length > 1) {
      setOtp((prev) => {
        const next = [...prev];
        raw.split("").slice(0, 6 - i).forEach((ch, offset) => { next[i + offset] = ch; });
        return next;
      });
      focusInput(Math.min(i + raw.length, 5));
      return;
    }
    setOtp((prev) => { const next = [...prev]; next[i] = raw; return next; });
    if (i < 5) focusInput(i + 1);
  }, [focusInput]);

  const handleOtpKeyDown = useCallback((e, i) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      setOtp((prev) => {
        const next = [...prev];
        if (next[i]) { next[i] = ""; return next; }
        if (i > 0)   { next[i - 1] = ""; focusInput(i - 1); }
        return next;
      });
    } else if (e.key === "ArrowLeft"  && i > 0) { focusInput(i - 1); }
      else if (e.key === "ArrowRight" && i < 5) { focusInput(i + 1); }
  }, [focusInput]);

  const handleOtpPaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    setOtp((prev) => {
      const next = [...prev];
      pasted.split("").forEach((ch, idx) => { next[idx] = ch; });
      return next;
    });
    focusInput(Math.min(pasted.length, 5));
  }, [focusInput]);

  // ── Step indicator ─────────────────────────────────────────────
  const stepIndex = {
    [STEPS.SEARCH]:  -1,
    [STEPS.FOUND]:    0,
    [STEPS.OTP]:      1,
    [STEPS.RESET]:    2,
    [STEPS.SUCCESS]:  3,
  }[step];

  const StepIndicator = () => (
    <div className="fp-steps">
      {STEP_LABELS.map((label, i) => {
        const cls = i < stepIndex ? "done" : i === stepIndex ? "active" : "";
        return (
          <div key={label} className={`fp-step ${cls}`}>
            <div className="fp-step-dot">
              {i < stepIndex ? <CheckCircleOutlined /> : i + 1}
            </div>
            <span className="fp-step-label">{label}</span>
          </div>
        );
      })}
    </div>
  );

  // ── Render steps ──────────────────────────────────────────────

  const SearchStep = () => (
    <>
      <h2 className="fp-form-title">Reset Your Password</h2>
      <p className="fp-form-sub">Enter your registered phone number to begin.</p>
      <Form form={searchForm} layout="vertical" onFinish={handleSearch} className="fp-input">
        <Form.Item name="searchPhone" label="Phone Number"
          rules={[{ required: true, message: "Phone number required" }]}>
          <Input prefix={<PhoneOutlined style={{ color: "#94a3b8" }} />}
            placeholder="+1 234 567 8900" size="large" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" block size="large"
            loading={isLoading} icon={<SearchOutlined />} className="fp-btn">
            {isLoading ? "Searching…" : "Search"}
          </Button>
        </Form.Item>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Link to="/portal-login" className="fp-link">Back to Login</Link>
        </div>
      </Form>
    </>
  );

  const FoundStep = () => (
    <>
      <StepIndicator />
      <h2 className="fp-form-title">Patient Found</h2>
      <p className="fp-form-sub">Confirm this is you before we send a reset code.</p>
      <div className="fp-patient-card">
        <div className="fp-patient-avatar"><UserOutlined /></div>
        <div style={{ flex: 1 }}>
          <div className="fp-patient-name">{patientData?.fullName}</div>
          <div className="fp-patient-meta">
            <span><MailOutlined style={{ marginRight: 5 }} />{maskEmail(patientData?.email)}</span>
            <span><PhoneOutlined style={{ marginRight: 5 }} />{maskPhone(patientData?.phone)}</span>
          </div>
        </div>
        <CheckCircleOutlined style={{ fontSize: 22, color: "#217559" }} />
      </div>
      <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20, lineHeight: 1.6 }}>
        We'll send a 6‑digit OTP to your registered email/phone.
      </p>
      <div style={{ display: "flex", gap: 12 }}>
        <Button block size="large" className="fp-btn-ghost" icon={<ArrowLeftOutlined />}
          onClick={() => { setStep(STEPS.SEARCH); setPatientData(null); }}>Back</Button>
        <Button type="primary" block size="large" loading={isLoading}
          icon={<ArrowRightOutlined />} className="fp-btn" onClick={handleSendOTP}>
          Send OTP
        </Button>
      </div>
    </>
  );

  const OTPStep = () => (
    <>
      <StepIndicator />
      <h2 className="fp-form-title">Verify Identity</h2>
      <p className="fp-form-sub" style={{ marginBottom: 28 }}>
        Enter the 6‑digit code sent to your contact.
        {patientData?.phone && (
          <span> We sent it to <strong>{maskPhoneLast4(patientData.phone)}</strong></span>
        )}
      </p>
      <div className="fp-otp-row">
        {otp.map((digit, i) => (
          <input
            key={i}
            ref={(el) => (otpRefs.current[i] = el)}
            value={digit}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={6}
            onInput={(e) => handleOtpInput(e, i)}
            onKeyDown={(e) => handleOtpKeyDown(e, i)}
            onPaste={handleOtpPaste}
            onFocus={(e) => e.target.select()}
            style={{
              width: 52, height: 58, textAlign: "center",
              fontSize: 22, fontWeight: 700, borderRadius: 12,
              border: `2px solid ${digit ? "#217559" : "#e2e8f0"}`,
              color: "#217559", background: digit ? "#f0fdf4" : "#fff",
              outline: "none", transition: "all 0.2s",
              caretColor: "transparent",
              WebkitTapHighlightColor: "transparent",
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
        ))}
      </div>

      <OTPTimerDisplay resetKey={otpResetKey} onResend={handleResendOTP} />

      <div style={{ display: "flex", gap: 12 }}>
        <Button block size="large" className="fp-btn-ghost" icon={<ArrowLeftOutlined />}
          onClick={() => setStep(STEPS.FOUND)}>Back</Button>
        <Button type="primary" block size="large" loading={isLoading}
          icon={<CheckCircleOutlined />} className="fp-btn" onClick={handleVerifyOTP}>
          Verify Code
        </Button>
      </div>
    </>
  );

  const ResetStep = () => (
    <>
      <StepIndicator />
      <h2 className="fp-form-title">Set New Password</h2>
      <p className="fp-form-sub">Create a new secure password for your account.</p>
      <Form form={passwordForm} layout="vertical" onFinish={handleResetPassword} className="fp-input">
        <Form.Item name="password" label="New Password"
          rules={[{ required: true, message: "Password required" }, { min: 8, message: "At least 8 characters" }]}>
          <Input.Password prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
            placeholder="Min. 8 characters"
            iconRender={(v) => (v ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            size="large" />
        </Form.Item>
        <Form.Item name="confirmPassword" label="Confirm Password"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Please confirm your password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) return Promise.resolve();
                return Promise.reject("Passwords do not match");
              },
            }),
          ]}>
          <Input.Password prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
            placeholder="Re-enter password"
            iconRender={(v) => (v ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            size="large" />
        </Form.Item>
        <div style={{ display: "flex", gap: 12 }}>
          <Button block size="large" className="fp-btn-ghost" icon={<ArrowLeftOutlined />}
            onClick={() => setStep(STEPS.OTP)}>Back</Button>
          <Button type="primary" htmlType="submit" block size="large"
            loading={isLoading} icon={<CheckCircleOutlined />} className="fp-btn">
            {isLoading ? "Resetting…" : "Reset Password"}
          </Button>
        </div>
      </Form>
    </>
  );

  const SuccessStep = () => (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <StepIndicator />
      <div className="fp-success-icon"><CheckCircleOutlined /></div>
      <h2 className="fp-form-title" style={{ textAlign: "center" }}>
        Password Reset Successful!
      </h2>
      <p className="fp-form-sub" style={{ textAlign: "center", marginBottom: 30 }}>
        Your password has been updated. You can now log in with your new password.
      </p>
      <Button type="primary" block size="large" icon={<LoginOutlined />}
        className="fp-btn" onClick={() => navigate("/portal-login", { replace: true })}>
        Go to Login
      </Button>
    </div>
  );

  // ── RENDER ────────────────────────────────────────────────────
  return (
    <>
      <style>{css}</style>
      <div className="fp-root">
        {/* Left panel – same branding */}
        <div className="fp-left">
          <div className="fp-brand-block">
             <div className="fp-logo-icon">
  <img
    src="/atantis-logo.png"
    alt="Atlantis Pediatric Hospital"
    className="pp-logo-image"
  />
</div>
            <h1 className="fp-brand-title">Atlantis Pediatric Hospital</h1>
            <p className="fp-brand-sub">Patient Portal · Secure Password Reset</p>
          </div>
          <div className="fp-features">
            <div className="fp-feat-item">
              <div className="fp-feat-icon"><SafetyOutlined /></div>
              <span>HIPAA Compliant &amp; Secure</span>
            </div>
            <div className="fp-feat-item">
              <div className="fp-feat-icon"><ClockCircleOutlined /></div>
              <span>24/7 Access to Your Health</span>
            </div>
            <div className="fp-feat-item">
              <div className="fp-feat-icon"><TeamOutlined /></div>
              <span>Connected to Your Provider</span>
            </div>
          </div>
          <div className="fp-left-footer">© 2025 Atlantis Pediatric Hospital. All rights reserved.</div>
        </div>

        {/* Right panel – form */}
        <div className="fp-right">
          <div className="fp-form-box">
            {step === STEPS.SEARCH && <SearchStep />}
            {step === STEPS.FOUND   && <FoundStep />}
            {step === STEPS.OTP     && <OTPStep />}
            {step === STEPS.RESET   && <ResetStep />}
            {step === STEPS.SUCCESS && <SuccessStep />}
          </div>
        </div>
      </div>
    </>
  );
}

export default ForgetPassword;